import { useTranslations } from '@vocab/react';
import { Box, Disclosure, Stack, Text } from 'braid-design-system';
import type { FC } from 'react';

import {
  QuestionnaireSubmissionStatusIcon,
  QuestionnaireSubmissionVerifiedTag,
} from '../../QuestionnaireSubmission';
import { SEEKPassIcon } from '../../QuestionnaireSubmission/SEEKPassIcon';
import type { Verification } from '../../QuestionnaireSubmission/types';
import { ProfileDetails } from '../ProfileDetails';

import translations from './.vocab';

import * as styles from './ApplicationQuestions.css';

interface Answer {
  id: string;
  text: string;
  isVerified: boolean;
}

interface ApplicationQuestionsProps {
  questions: Array<{
    question: string;
    status: string;
    answers: Answer[];
    verifications: Verification[];
    showVerifiedTagInAnswers: boolean;
  }>;
  allVerifications: Verification[];
}

export const ApplicationQuestions: FC<ApplicationQuestionsProps> = ({
  questions,
  allVerifications,
}) => {
  const { t } = useTranslations(translations);

  return (
    <Stack space="medium">
      <Box component="dl" className={styles.detailList}>
        {questions.map(
          (
            {
              question,
              answers,
              status,
              verifications,
              showVerifiedTagInAnswers,
            },
            index,
          ) => {
            const verifiedAnswerTags = showVerifiedTagInAnswers
              ? answers
                  .filter(({ isVerified }) => isVerified)
                  .map((answer) => (
                    <QuestionnaireSubmissionVerifiedTag
                      key={answer.id}
                      answerText={answer.text}
                      hideTooltip
                      verifications={verifications}
                    />
                  ))
              : null;

            const answersWithoutTags = showVerifiedTagInAnswers
              ? answers.filter(({ isVerified }) => !isVerified)
              : answers;

            return (
              <Box
                component="div"
                key={index}
                className={styles.descriptionTermDetailsGroup}
              >
                <Box
                  component="dt"
                  name={question}
                  className={styles.column1}
                  paddingRight="medium"
                >
                  <Text size="small" tone="secondary">
                    {question}
                  </Text>
                </Box>
                <Box component="dd" className={styles.column2}>
                  {status !== 'CUSTOM' ? (
                    <QuestionnaireSubmissionStatusIcon
                      hideTooltip
                      status={status}
                      verifications={verifications}
                    />
                  ) : null}
                  {verifiedAnswerTags}
                  <Text size="small">
                    {answersWithoutTags.map(({ text }) => text).join(', ')}
                  </Text>
                </Box>
              </Box>
            );
          },
        )}
      </Box>

      {allVerifications.length > 0 ? (
        <Disclosure
          id="rr-verification-disclosures"
          size="small"
          weight="weak"
          expandLabel={t('Show verification details')}
          collapseLabel={t('Hide verification details')}
          data={{
            cy: 'show-application-questions-verifications',
          }}
        >
          <Stack space="medium">
            <SEEKPassIcon />
            {allVerifications.map((verification, index) => (
              <Stack space="small" key={index}>
                <Text size="small" weight="strong">
                  {verification.title}
                </Text>
                <ProfileDetails details={verification.credentialInfo} />
              </Stack>
            ))}
          </Stack>
        </Disclosure>
      ) : null}
    </Stack>
  );
};
